import { getAllQuestionnaireAnswers, LOINC, MedplumClient, parseReference } from "@medplum/core";
import {
  Annotation,
  Bundle,
  BundleEntry,
  BundleEntryRequest,
  CodeableConcept,
  Observation,
  Patient,
  QuestionnaireResponse,
} from "@medplum/fhirtypes";

import isNumber from "lodash/isNumber";
import { v4 as uuidv4 } from "uuid";

import { SentryInstance } from "@/bots/utils/sentry";
import { getQuestionnaireResponseItems } from "@/utils/medplum";

import {
  AnswerBuilder,
  Answers,
  createObservation,
  get,
  getAnswer,
  getQuestionnaireFormsetResponses,
  itemIsAccepted,
  logBundleEntryError,
  ObservationValues,
  Systems,
} from "../utils";

enum LOINCCodes {
  NumberOfFetuses = "55281-0",
  OutcomeOfPregnancy = "63893-2",
  TotalPregnancies = "11996-6",
  TotalFullTerm = "11639-2",
  TotalPremature = "11637-6",
  TotalAbortionsInduced = "11613-7",
  TotalMiscarriage = "11614-5",
  TotalAbortionsAndMiscarriages = "11612-9",
  TotalLivingChildren = "11638-4",
}

const LOINCDisplay = {
  [LOINCCodes.NumberOfFetuses]: "[#] Fetuses",
  [LOINCCodes.OutcomeOfPregnancy]: "Outcome of pregnancy",
  [LOINCCodes.TotalPregnancies]: "[#] Pregnancies",
  [LOINCCodes.TotalFullTerm]: "[#] Births.term",
  [LOINCCodes.TotalPremature]: "[#] Births.preterm",
  [LOINCCodes.TotalAbortionsInduced]: "[#] Abortions.induced",
  [LOINCCodes.TotalMiscarriage]: "[#] Abortions.spontaneous",
  [LOINCCodes.TotalAbortionsAndMiscarriages]: "[#] Abortions.total",
  [LOINCCodes.TotalLivingChildren]: "[#] Births.still living",
} as const;

const QHDisplay = {
  pastPregnanciesOccurrences: "Total past pregnancies",
  pastPregnanciesHistoryFormsetFetusesCount: "Past pregnancy history fetuses count",
  pastPregnanciesHistoryFormsetGeneticParent:
    "Genetic parent of this past pregnancy is the same as for the current pregnancy",
  PastPregnanciesFullTerm: "Full term pregnancies",
  PastPregnanciesPremature: "Premature births",
  PastPregnanciesAbortions: "Abortions",
  PastPregnanciesMiscarriages: "Miscarriages",
  PastPregnanciesAbortionsAndMiscarriages: "Total abortions and miscarriages",
  PastPregnanciesLivingChildren: "Living children",
  PastPregnanciesComments: "Past pregnancies comments",
} as const;

export enum AnswersOptions {
  pastPregnanciesOccurrences = "pastPregnanciesOccurrences",
  PastPregnanciesFullTerm = "pastPregnanciesFullTerm",
  PastPregnanciesPremature = "pastPregnanciesPremature",
  PastPregnanciesAbortions = "pastPregnanciesAbortions",
  PastPregnanciesMiscarriages = "pastPregnanciesMiscarriages",
  PastPregnanciesAbortionsAndMiscarriages = "pastPregnanciesTotalAbortions",
  PastPregnanciesLivingChildren = "pastPregnanciesLivingChildren",
  PastPregnanciesComments = "pastPregnanciesComments",
  PastPregnanciesHistoryFormset = "pastPregnanciesHistoryFormset",
  PastPregnanciesHistoryFormsetCode = "pastPregnanciesHistoryFormsetCode",
  pastPregnanciesHistoryFormsetFetusesCount = "pastPregnanciesHistoryFormsetFetusesCount",
  pastPregnanciesHistoryFormsetOutcome = "pastPregnanciesHistoryFormsetOutcome",
  pastPregnanciesHistoryFormsetGeneticParent = "pastPregnanciesHistoryFormsetGeneticParent",
  pastPregnanciesHistoryFormsetAdditionalFacts = "pastPregnanciesHistoryFormsetAdditionalFacts",
}

const ObservationCode: Record<string, CodeableConcept> = {
  [AnswersOptions.pastPregnanciesOccurrences]: {
    coding: [
      {
        system: Systems.QH,
        code: AnswersOptions.pastPregnanciesOccurrences,
        display: QHDisplay.pastPregnanciesOccurrences,
      },
      {
        system: LOINC,
        code: LOINCCodes.TotalPregnancies,
        display: LOINCDisplay[LOINCCodes.TotalPregnancies],
      },
    ],
  },
  [AnswersOptions.pastPregnanciesHistoryFormsetFetusesCount]: {
    coding: [
      {
        system: Systems.QH,
        code: AnswersOptions.pastPregnanciesHistoryFormsetFetusesCount,
        display: QHDisplay.pastPregnanciesHistoryFormsetFetusesCount,
      },
      {
        system: LOINC,
        code: LOINCCodes.NumberOfFetuses,
        display: LOINCDisplay[LOINCCodes.NumberOfFetuses],
      },
    ],
  },
  [AnswersOptions.pastPregnanciesHistoryFormsetGeneticParent]: {
    coding: [
      {
        system: Systems.QH,
        code: AnswersOptions.pastPregnanciesHistoryFormsetGeneticParent,
        display: QHDisplay.pastPregnanciesHistoryFormsetGeneticParent,
      },
    ],
  },
  [AnswersOptions.pastPregnanciesHistoryFormsetOutcome]: {
    coding: [
      {
        system: LOINC,
        code: LOINCCodes.OutcomeOfPregnancy,
        display: LOINCDisplay[LOINCCodes.OutcomeOfPregnancy],
      },
    ],
  },
  [AnswersOptions.PastPregnanciesComments]: {
    coding: [
      {
        system: Systems.QH,
        code: AnswersOptions.PastPregnanciesComments,
        display: QHDisplay.PastPregnanciesComments,
      },
    ],
  },
  [AnswersOptions.PastPregnanciesAbortions]: {
    coding: [
      {
        system: Systems.QH,
        code: AnswersOptions.PastPregnanciesAbortions,
        display: QHDisplay.PastPregnanciesAbortions,
      },
      {
        system: LOINC,
        code: LOINCCodes.TotalAbortionsInduced,
        display: LOINCDisplay[LOINCCodes.TotalAbortionsInduced],
      },
    ],
  },
  [AnswersOptions.PastPregnanciesPremature]: {
    coding: [
      {
        system: Systems.QH,
        code: AnswersOptions.PastPregnanciesPremature,
        display: QHDisplay.PastPregnanciesPremature,
      },
      {
        system: LOINC,
        code: LOINCCodes.TotalPremature,
        display: LOINCDisplay[LOINCCodes.TotalPremature],
      },
    ],
  },
  [AnswersOptions.PastPregnanciesFullTerm]: {
    coding: [
      {
        system: Systems.QH,
        code: AnswersOptions.PastPregnanciesFullTerm,
        display: QHDisplay.PastPregnanciesFullTerm,
      },
      {
        system: LOINC,
        code: LOINCCodes.TotalFullTerm,
        display: LOINCDisplay[LOINCCodes.TotalFullTerm],
      },
    ],
  },
  [AnswersOptions.PastPregnanciesMiscarriages]: {
    coding: [
      {
        system: Systems.QH,
        code: AnswersOptions.PastPregnanciesMiscarriages,
        display: QHDisplay.PastPregnanciesMiscarriages,
      },
      {
        system: LOINC,
        code: LOINCCodes.TotalMiscarriage,
        display: LOINCDisplay[LOINCCodes.TotalMiscarriage],
      },
    ],
  },
  [AnswersOptions.PastPregnanciesAbortionsAndMiscarriages]: {
    coding: [
      {
        system: Systems.QH,
        code: AnswersOptions.PastPregnanciesAbortionsAndMiscarriages,
        display: QHDisplay.PastPregnanciesAbortionsAndMiscarriages,
      },
      {
        system: LOINC,
        code: LOINCCodes.TotalAbortionsAndMiscarriages,
        display: LOINCDisplay[LOINCCodes.TotalAbortionsAndMiscarriages],
      },
    ],
  },
  [AnswersOptions.PastPregnanciesLivingChildren]: {
    coding: [
      {
        system: Systems.QH,
        code: AnswersOptions.PastPregnanciesLivingChildren,
        display: QHDisplay.PastPregnanciesLivingChildren,
      },
      {
        system: LOINC,
        code: LOINCCodes.TotalLivingChildren,
        display: LOINCDisplay[LOINCCodes.TotalLivingChildren],
      },
    ],
  },
};

// Helper functions

function createObservationBundleEntry(
  observationAnswer: AnswersOptions,
  observationValues: ObservationValues,
  patient: Patient,
  fullUrl?: string,
  request = {
    method: "POST",
    url: "Observation",
  } as BundleEntryRequest,
): BundleEntry<Observation> {
  const code = ObservationCode[observationAnswer];

  return {
    resource: createObservation(code, observationValues, patient),
    request,
    ...(fullUrl && { fullUrl }),
  };
}

// Past Pregnancies Observation Functions
function addPastPregnanciesTotalPregnanciesObservation(
  answers: Answers,
  observations: BundleEntry<Observation>[],
  profile: Patient,
  questionnaireResponse: QuestionnaireResponse,
) {
  const matchingItems = getQuestionnaireResponseItems(
    AnswersOptions.pastPregnanciesOccurrences,
    questionnaireResponse.item,
    false,
  );
  if (!itemIsAccepted(matchingItems)) return;
  const pastPregnanciesTotal = get(answers[AnswersOptions.pastPregnanciesOccurrences]) as number;

  if (!isNumber(pastPregnanciesTotal)) return;

  const pastPregnanciesTotalObservation = createObservationBundleEntry(
    AnswersOptions.pastPregnanciesOccurrences,
    { valueInteger: pastPregnanciesTotal },
    profile,
  );
  observations.push(pastPregnanciesTotalObservation);
}

function addPastPregnanciesTotalFullTermObservation(
  answers: Answers,
  observations: BundleEntry<Observation>[],
  profile: Patient,
  questionnaireResponse: QuestionnaireResponse,
) {
  const matchingItems = getQuestionnaireResponseItems(
    AnswersOptions.PastPregnanciesFullTerm,
    questionnaireResponse.item,
    false,
  );
  if (!itemIsAccepted(matchingItems)) return;

  const pastPregnanciesFullTerm = get(answers[AnswersOptions.PastPregnanciesFullTerm]) as number;

  if (!isNumber(pastPregnanciesFullTerm)) return;

  const pastPregnanciesFullTermObservation = createObservationBundleEntry(
    AnswersOptions.PastPregnanciesFullTerm,
    { valueInteger: pastPregnanciesFullTerm },
    profile,
  );
  observations.push(pastPregnanciesFullTermObservation);
}

function addPastPregnanciesTotalPrematureObservation(
  answers: Answers,
  observations: BundleEntry<Observation>[],
  profile: Patient,
  questionnaireResponse: QuestionnaireResponse,
) {
  const matchingItems = getQuestionnaireResponseItems(
    AnswersOptions.PastPregnanciesPremature,
    questionnaireResponse.item,
    false,
  );
  if (!itemIsAccepted(matchingItems)) return;

  const pastPregnanciesPremature = get(answers[AnswersOptions.PastPregnanciesPremature]) as number;

  if (!pastPregnanciesPremature) return;

  const pastPregnanciesPrematureObservation = createObservationBundleEntry(
    AnswersOptions.PastPregnanciesPremature,
    { valueInteger: pastPregnanciesPremature },
    profile,
  );
  observations.push(pastPregnanciesPrematureObservation);
}

function addPastPregnanciesTotalAbortionsObservation(
  answers: Answers,
  observations: BundleEntry<Observation>[],
  profile: Patient,
  questionnaireResponse: QuestionnaireResponse,
) {
  const matchingItems = getQuestionnaireResponseItems(
    AnswersOptions.PastPregnanciesAbortions,
    questionnaireResponse.item,
    false,
  );
  if (!itemIsAccepted(matchingItems)) return;

  const pastPregnanciesAbortions = get(answers[AnswersOptions.PastPregnanciesAbortions]) as number;

  if (!isNumber(pastPregnanciesAbortions)) return;

  const pastPregnanciesAbortionsObservation = createObservationBundleEntry(
    AnswersOptions.PastPregnanciesAbortions,
    { valueInteger: pastPregnanciesAbortions },
    profile,
  );
  observations.push(pastPregnanciesAbortionsObservation);
}

function addPastPregnanciesTotalMiscarriagesObservation(
  answers: Answers,
  observations: BundleEntry<Observation>[],
  profile: Patient,
  questionnaireResponse: QuestionnaireResponse,
) {
  const matchingItems = getQuestionnaireResponseItems(
    AnswersOptions.PastPregnanciesMiscarriages,
    questionnaireResponse.item,
    false,
  );
  if (!itemIsAccepted(matchingItems)) return;

  const pastPregnanciesMiscarriages = get(
    answers[AnswersOptions.PastPregnanciesMiscarriages],
  ) as number;

  if (!isNumber(pastPregnanciesMiscarriages)) return;

  const pastPregnanciesMiscarriagesObservation = createObservationBundleEntry(
    AnswersOptions.PastPregnanciesMiscarriages,
    { valueInteger: pastPregnanciesMiscarriages },
    profile,
  );
  observations.push(pastPregnanciesMiscarriagesObservation);
}

function addPastPregnanciesTotalAbortionsAndMiscarriagesObservation(
  answers: Answers,
  observations: BundleEntry<Observation>[],
  profile: Patient,
  questionnaireResponse: QuestionnaireResponse,
) {
  const matchingAbortionItems = getQuestionnaireResponseItems(
    AnswersOptions.PastPregnanciesAbortions,
    questionnaireResponse.item,
    false,
  );
  const matchingMiscarriageItems = getQuestionnaireResponseItems(
    AnswersOptions.PastPregnanciesMiscarriages,
    questionnaireResponse.item,
    false,
  );
  if (!itemIsAccepted(matchingAbortionItems) && !itemIsAccepted(matchingMiscarriageItems)) return;

  let pastPregnanciesAbortions = get(answers[AnswersOptions.PastPregnanciesAbortions]) as number;
  let pastPregnanciesMiscarriages = get(
    answers[AnswersOptions.PastPregnanciesMiscarriages],
  ) as number;
  if (!isNumber(pastPregnanciesAbortions) || !itemIsAccepted(matchingAbortionItems)) {
    pastPregnanciesAbortions = 0;
  }
  if (!isNumber(pastPregnanciesMiscarriages) || !itemIsAccepted(matchingMiscarriageItems)) {
    pastPregnanciesMiscarriages = 0;
  }
  const pastPregnanciesAbortionsAndMiscarriages =
    pastPregnanciesAbortions + pastPregnanciesMiscarriages;

  const pastPregnanciesAbortionsAndMiscarriagesObservation = createObservationBundleEntry(
    AnswersOptions.PastPregnanciesAbortionsAndMiscarriages,
    { valueInteger: pastPregnanciesAbortionsAndMiscarriages },
    profile,
  );
  observations.push(pastPregnanciesAbortionsAndMiscarriagesObservation);
}

function addPastPregnanciesTotalLivingChildrenObservation(
  answers: Answers,
  observations: BundleEntry<Observation>[],
  profile: Patient,
  questionnaireResponse: QuestionnaireResponse,
) {
  const matchingItems = getQuestionnaireResponseItems(
    AnswersOptions.PastPregnanciesLivingChildren,
    questionnaireResponse.item,
    false,
  );
  if (!itemIsAccepted(matchingItems)) return;

  const pastPregnanciesLivingChildren = get(
    answers[AnswersOptions.PastPregnanciesLivingChildren],
  ) as number;

  if (!isNumber(pastPregnanciesLivingChildren)) return;

  const pastPregnanciesLivingChildrenObservation = createObservationBundleEntry(
    AnswersOptions.PastPregnanciesLivingChildren,
    { valueInteger: pastPregnanciesLivingChildren },
    profile,
  );
  observations.push(pastPregnanciesLivingChildrenObservation);
}

function addPastPregnanciesCommentsObservation(
  answers: Answers,
  observations: BundleEntry<Observation>[],
  profile: Patient,
) {
  const pastPregnanciesComments = get(answers[AnswersOptions.PastPregnanciesComments]) as string;

  const pastPregnanciesCommentsObservation = createObservationBundleEntry(
    AnswersOptions.PastPregnanciesComments,
    { valueString: pastPregnanciesComments },
    profile,
  );
  observations.push(pastPregnanciesCommentsObservation);
}

// Main function to get all past pregnancies observations
function getClientPastPregnanciesObservations(
  answers: Answers,
  profile: Patient,
  questionnaireResponse: QuestionnaireResponse,
): BundleEntry<Observation>[] {
  const observations: BundleEntry<Observation>[] = [];

  const hasPastPregnancies = getAnswer(
    questionnaireResponse,
    AnswersOptions.pastPregnanciesOccurrences,
  )?.valueInteger;

  if (!hasPastPregnancies) {
    return observations;
  }

  addPastPregnanciesTotalPregnanciesObservation(
    answers,
    observations,
    profile,
    questionnaireResponse,
  );
  addPastPregnanciesTotalFullTermObservation(answers, observations, profile, questionnaireResponse);
  addPastPregnanciesTotalPrematureObservation(
    answers,
    observations,
    profile,
    questionnaireResponse,
  );
  addPastPregnanciesTotalAbortionsObservation(
    answers,
    observations,
    profile,
    questionnaireResponse,
  );
  addPastPregnanciesTotalMiscarriagesObservation(
    answers,
    observations,
    profile,
    questionnaireResponse,
  );
  addPastPregnanciesTotalAbortionsAndMiscarriagesObservation(
    answers,
    observations,
    profile,
    questionnaireResponse,
  );
  addPastPregnanciesTotalLivingChildrenObservation(
    answers,
    observations,
    profile,
    questionnaireResponse,
  );
  addPastPregnanciesCommentsObservation(answers, observations, profile);

  const formsetAnswers = getQuestionnaireFormsetResponses(
    questionnaireResponse,
    AnswersOptions.PastPregnanciesHistoryFormset,
    AnswersOptions.PastPregnanciesHistoryFormsetCode,
  );

  for (const formsetAnswer of formsetAnswers) {
    const answerBuilder = new AnswerBuilder(formsetAnswer);

    const fullUrlReferences: string[] = [];

    const pregnancyHistoryFetusesCount = answerBuilder.valueInteger(
      AnswersOptions.pastPregnanciesHistoryFormsetFetusesCount,
    );
    if (pregnancyHistoryFetusesCount) {
      const observationFullUrl = `urn:uuid:${uuidv4()}`;
      fullUrlReferences.push(observationFullUrl);

      const fetusesCountObservation = createObservationBundleEntry(
        AnswersOptions.pastPregnanciesHistoryFormsetFetusesCount,
        { valueInteger: pregnancyHistoryFetusesCount },
        profile,
        observationFullUrl,
      );

      observations.push(fetusesCountObservation);
    }

    const pregnancyHistoryIsGeneticParent = answerBuilder.valueYesNoToBoolean(
      AnswersOptions.pastPregnanciesHistoryFormsetGeneticParent,
    );

    if (pregnancyHistoryIsGeneticParent !== undefined) {
      const observationFullUrl = `urn:uuid:${uuidv4()}`;
      fullUrlReferences.push(observationFullUrl);

      const geneticParentObservation = createObservationBundleEntry(
        AnswersOptions.pastPregnanciesHistoryFormsetGeneticParent,
        { valueBoolean: pregnancyHistoryIsGeneticParent },
        profile,
        observationFullUrl,
      );

      observations.push(geneticParentObservation);
    }

    const outcomeObservationNotes: Annotation[] = [];

    const pregnancyHistoryAdditionalFacts = answerBuilder.valueString(
      AnswersOptions.pastPregnanciesHistoryFormsetAdditionalFacts,
    );

    if (pregnancyHistoryAdditionalFacts) {
      outcomeObservationNotes.push({
        text: pregnancyHistoryAdditionalFacts,
      });
    }

    const pregnancyHistoryOutcome = answerBuilder.valueCoding(
      AnswersOptions.pastPregnanciesHistoryFormsetOutcome,
    );

    if (pregnancyHistoryOutcome) {
      const outcomeObservationValues: ObservationValues = {
        valueCodeableConcept: {
          coding: [pregnancyHistoryOutcome],
        },
        hasMember: fullUrlReferences.map((reference) => ({ reference })),
      };

      if (outcomeObservationNotes?.length > 0) {
        outcomeObservationValues.note = outcomeObservationNotes;
      }

      const pastPregnancyOutcomeObservation = createObservationBundleEntry(
        AnswersOptions.pastPregnanciesHistoryFormsetOutcome,
        outcomeObservationValues,
        profile,
      );

      observations.push(pastPregnancyOutcomeObservation);
    }
  }

  return observations;
}

export async function processPastPregnancies(
  medplum: MedplumClient,
  questionnaireResponse: QuestionnaireResponse,
  sentry: SentryInstance,
): Promise<boolean> {
  const [_, clientId] = parseReference(questionnaireResponse.subject);

  const profile = await medplum.readResource("Patient", clientId);

  if (!profile) throw new Error("Client does not exist");

  const answers = getAllQuestionnaireAnswers(questionnaireResponse);

  const pastPregnanciesObservations = getClientPastPregnanciesObservations(
    answers,
    profile,
    questionnaireResponse,
  );

  const batchBundle: Bundle = {
    resourceType: "Bundle",
    type: "batch",
    entry: [...pastPregnanciesObservations],
  };

  if (batchBundle.entry?.length) {
    const response = await medplum.executeBatch(batchBundle);

    response.entry?.forEach((entry) => {
      const entrySuccess = entry.response?.status[0] === "2";
      if (!entrySuccess) {
        logBundleEntryError(entry, "Past Pregnancies", sentry);
      }
    });
  }

  return true;
}
